import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2, FileText, Eye, Plus, Bell } from "lucide-react";
import { ClaimsAssistant } from "@/components/ClaimsAssistant";

interface Claim {
  id: string;
  claim_number: string;
  policyholder_name: string;
  claim_type: string;
  loss_date: string;
  status: string;
  created_at: string;
  settlement_amount: number | null;
  request_type: string | null;
}

const REQUEST_TYPE_LABELS: Record<string, string> = {
  "housing": "Housing Only",
  "housing-and-hotel": "Housing + Hotel",
  "hotel-only": "Hotel Only",
};

const AdjusterDashboard = () => {
  const navigate = useNavigate();
  const { impersonatedUser, isImpersonating } = useImpersonation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState<Claim[]>([]);

  const effectiveUserId = isImpersonating ? impersonatedUser?.id : user?.id;

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (effectiveUserId) {
      fetchClaims();
    }
  }, [effectiveUserId]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      setUser(session.user);

      if (!isImpersonating) {
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .eq("role", "adjuster")
          .maybeSingle();

        if (!roleData) {
          toast.error("Access denied. Adjuster privileges required.");
          navigate("/dashboard");
          return;
        }
      }
    } catch (error: any) {
      console.error("Error checking auth:", error);
      toast.error("Authentication error");
    } finally {
      setLoading(false);
    }
  };

  const fetchClaims = async () => {
    try {
      const { data, error } = await supabase
        .from("claims")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setClaims(data || []);
    } catch (error: any) {
      console.error("Error fetching claims:", error);
      toast.error("Failed to load claims");
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "outline",
      in_review: "secondary",
      approved: "default",
      denied: "destructive"
    };
    return variants[status] || "outline";
  };

  const getStatusLabel = (status: string) => {
    if (status === "pending") return "Pending";
    if (status === "denied") return "Closed";
    // in_review, approved, or any other accepted state
    return "Active";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="max-w-7xl mx-auto space-y-8">
          <div>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <FileText className="h-8 w-8" />
              Adjuster Dashboard
            </h1>
            <p className="text-muted-foreground mt-2">
              Review and manage placement requests
            </p>
          </div>

          {/* Stats Overview and Quick Actions */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="md:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{claims.length}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  All placement requests
                </p>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="md:col-span-3">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks and shortcuts</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                <Button
                  className="justify-start h-auto p-4"
                  onClick={() => navigate("/claims")}
                >
                  <div className="flex items-start gap-3 text-left">
                    <Plus className="h-5 w-5 mt-0.5" />
                    <div>
                      <div className="font-semibold">I want to submit a new placement request</div>
                      <div className="text-xs text-primary-foreground">Start a new placement request</div>
                    </div>
                  </div>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Urgent Items */}
          {claims.filter(c => c.status === "pending").length > 0 && (
            <Card className="border-orange-500">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-orange-500" />
                  <CardTitle>Urgent: Pending Requests</CardTitle>
                </div>
                <CardDescription>
                  {claims.filter(c => c.status === "pending").length} requests need your attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {claims.filter(c => c.status === "pending").slice(0, 3).map((claim) => (
                    <div key={claim.id} className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950 rounded-lg">
                      <div>
                        <p className="font-medium">{claim.claim_number}</p>
                        <p className="text-sm text-muted-foreground">{claim.policyholder_name}</p>
                      </div>
                      <Button size="sm" onClick={() => navigate(`/claim/${claim.id}`)}>
                        Review
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(() => {
            const sections: { key: string; title: string; description: string; items: Claim[] }[] = [
              {
                key: "pending",
                title: "Pending",
                description: "Submitted, not yet accepted by TPS Housing",
                items: claims.filter((c) => c.status === "pending"),
              },
              {
                key: "active",
                title: "Active",
                description: "Accepted by TPS Housing and in progress",
                items: claims.filter((c) => c.status === "in_review" || c.status === "approved"),
              },
              {
                key: "closed",
                title: "Closed",
                description: "Closed placement requests",
                items: claims.filter((c) => c.status === "denied"),
              },
            ];

            return sections.map((section) => (
              <Card key={section.key}>
                <CardHeader>
                  <CardTitle>{section.title} Placement Requests</CardTitle>
                  <CardDescription>
                    {section.description} ({section.items.length})
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Claim #</TableHead>
                        <TableHead>Policyholder</TableHead>
                        <TableHead>Loss Type</TableHead>
                        <TableHead>Placement Request Type</TableHead>
                        <TableHead>Loss Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>ALE Limit</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {section.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No {section.title.toLowerCase()} placement requests.
                          </TableCell>
                        </TableRow>
                      ) : (
                        section.items.map((claim) => (
                          <TableRow key={claim.id}>
                            <TableCell className="font-medium">{claim.claim_number}</TableCell>
                            <TableCell>{claim.policyholder_name}</TableCell>
                            <TableCell>{claim.claim_type}</TableCell>
                            <TableCell>
                              {claim.request_type ? (REQUEST_TYPE_LABELS[claim.request_type] || claim.request_type) : "-"}
                            </TableCell>
                            <TableCell>{new Date(claim.loss_date).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Badge variant={getStatusBadge(claim.status)}>
                                {getStatusLabel(claim.status)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {claim.settlement_amount ? `$${claim.settlement_amount.toLocaleString()}` : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/claim/${claim.id}`)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ));
          })()}
        </div>
      </main>

      <Footer />

      {/* Claims Assistant Chatbot */}
      <ClaimsAssistant />
    </div>
  );
};

export default AdjusterDashboard;
